import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useAuthStore } from '@/stores/auth.store'
import { messagesApi, ApiError } from '@/lib/messages-api'
import { trackEvent, Events } from '@/lib/observability'
import { MessageBubble, DateSeparator } from '@/components/messages/MessageBubble'
import { MessageInput } from '@/components/messages/MessageInput'
import type { ConversationDetail, Message } from '@/types/message'

export function ConversationPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)

  const [conversation, setConversation] = useState<ConversationDetail | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  const fetchConversation = useCallback(async (isInitial = false) => {
    if (!id) return

    try {
      if (isInitial) setLoading(true)
      const response = await messagesApi.getConversation(id)
      setConversation(response.conversation)
      setMessages(response.messages)
      setHasMore(response.hasMore)
      setError(null)

      // Track conversation opened (only on initial load)
      if (isInitial) {
        trackEvent(Events.CONVERSATION_OPENED, {
          conversationType: response.conversation.type,
          participantCount: response.conversation.participants.length,
        })
      }

      // Mark as read
      await messagesApi.markAsRead(id)
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setError('Conversation not found')
      } else if (err instanceof ApiError && err.status === 403) {
        setError('You do not have access to this conversation')
      } else {
        setError('Failed to load conversation')
      }
      console.error('Error fetching conversation:', err)
    } finally {
      if (isInitial) setLoading(false)
    }
  }, [id])

  const loadMoreMessages = async () => {
    if (!id || !hasMore || loadingMore || messages.length === 0) return

    try {
      setLoadingMore(true)
      const oldestMessage = messages[0]
      const response = await messagesApi.getConversation(id, {
        before: oldestMessage.id,
        limit: 50,
      })
      setMessages((prev) => [...response.messages, ...prev])
      setHasMore(response.hasMore)
    } catch (err) {
      console.error('Error loading more messages:', err)
    } finally {
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    fetchConversation(true)

    // Poll for new messages every 10 seconds
    const interval = setInterval(() => fetchConversation(false), 10000)
    return () => clearInterval(interval)
  }, [fetchConversation])

  // Scroll to bottom on initial load and when new messages arrive
  useEffect(() => {
    if (!loading && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView()
    }
  }, [loading, messages.length])

  const handleSend = async (content: string) => {
    if (!id || !conversation) return

    try {
      setSending(true)
      const response = await messagesApi.sendMessage(id, { content })
      setMessages((prev) => [...prev, response.message])

      // Track message sent (no content, just metadata)
      trackEvent(Events.MESSAGE_SENT, {
        conversationType: conversation.type,
      })

      // Scroll to bottom
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    } catch (err) {
      console.error('Error sending message:', err)
      alert('Failed to send message. Please try again.')
    } finally {
      setSending(false)
    }
  }

  const getDisplayName = () => {
    if (!conversation || !user) return ''

    if (conversation.name) {
      return conversation.name
    }

    if (conversation.type === 'direct') {
      const otherParticipant = conversation.participants.find(
        (p) => p.userId !== user.id
      )
      return otherParticipant?.name || 'Unknown'
    }

    if (conversation.type === 'broadcast') {
      return 'Broadcast'
    }

    // Group chat
    const otherNames = conversation.participants
      .filter((p) => p.userId !== user.id)
      .map((p) => p.name)
      .slice(0, 3)

    if (otherNames.length > 3) {
      return `${otherNames.join(', ')} +${conversation.participants.length - 4}`
    }
    return otherNames.join(', ') || 'Group'
  }

  const getSubtitle = () => {
    if (!conversation) return ''

    if (conversation.type === 'broadcast') {
      return conversation.allowReplies ? 'Broadcast' : 'Announcement (no replies)'
    }

    if (conversation.type === 'group') {
      return `${conversation.participants.length} participants`
    }

    return ''
  }

  // Check if user can send messages
  const canSendMessage = () => {
    if (!conversation || !user) return false

    // Broadcast without replies
    if (conversation.type === 'broadcast' && !conversation.allowReplies) {
      return conversation.childminderId === user.id
    }

    return true
  }

  // Group messages by date
  const groupedMessages = messages.reduce<{ date: string; messages: Message[] }[]>(
    (groups, message) => {
      const date = new Date(message.createdAt).toDateString()
      const lastGroup = groups[groups.length - 1]

      if (lastGroup && lastGroup.date === date) {
        lastGroup.messages.push(message)
      } else {
        groups.push({ date, messages: [message] })
      }

      return groups
    },
    []
  )

  if (!user) return null

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex-none border-b border-gray-100 bg-white px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/messages')}
            className="p-1 -ml-1 text-gray-600 hover:text-gray-900"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold text-gray-900 truncate ph-no-capture">{getDisplayName()}</h1>
            {getSubtitle() && (
              <p className="text-xs text-gray-500 truncate">{getSubtitle()}</p>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <p className="text-red-600 mb-2">{error}</p>
            <button
              onClick={() => navigate('/messages')}
              className="text-primary-600 underline"
            >
              Back to messages
            </button>
          </div>
        </div>
      ) : (
        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto px-4 py-2"
        >
          {/* Load more button */}
          {hasMore && (
            <div className="text-center py-2">
              <button
                onClick={loadMoreMessages}
                disabled={loadingMore}
                className="text-sm text-primary-600 hover:underline disabled:opacity-50"
              >
                {loadingMore ? 'Loading...' : 'Load earlier messages'}
              </button>
            </div>
          )}

          {/* Messages grouped by date */}
          {groupedMessages.map((group) => (
            <div key={group.date}>
              <DateSeparator date={group.messages[0].createdAt} />
              {group.messages.map((message, index) => {
                // Show sender name in group/broadcast chats for messages from others
                const showSender =
                  conversation?.type !== 'direct' &&
                  !message.isMine &&
                  (index === 0 ||
                    group.messages[index - 1].senderId !== message.senderId)

                return (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    showSender={showSender}
                  />
                )
              })}
            </div>
          ))}

          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Input */}
      {!loading && !error && (
        canSendMessage() ? (
          <MessageInput
            onSend={handleSend}
            disabled={sending}
            placeholder={sending ? 'Sending...' : 'Type a message...'}
          />
        ) : (
          <div className="flex-none p-4 border-t bg-gray-50 text-center text-sm text-gray-500">
            Replies are disabled for this announcement
          </div>
        )
      )}
    </div>
  )
}
